import { useMemo, useState } from 'react';
import { Loader2, Plus, Wifi } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import SearchBar from '@/shared/components/SearchBar';
import EmptyView from '@/shared/components/EmptyView';
import RouterConfigCard from '@/domains/routers/components/RouterConfigCard';
import RouterConfigDialog from '@/domains/routers/dialogs/RouterConfigDialog';
import {
  usePaginatedRouterConfigs,
} from '@/domains/routers/services/account-routers';
import { parseRouterConfigSettings, RouterConfig } from '@/shared/types/routerTypes';
import { Feature, Permission } from '@/shared/types/permissionsTypes';
import { OrgType } from '@/shared/types/userRoleTypes';
import { usePermissions } from '@/shared/hooks/use-permissions';

export const RouterConfigsTab = () => {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(OrgType.TENANT, Feature.ROUTER_CONFIGS, Permission.MANAGE);

  const {
    data: routerConfigsInfiniteData,
    isLoading: isLoadingRouterConfigs,
    isFetchingNextPage: isFetchingNextRouterConfigs,
    fetchNextPage: fetchNextRouterConfigs,
    hasNextPage: hasNextRouterConfigs,
  } = usePaginatedRouterConfigs();

  const mergedRouterConfigs = useMemo(() => {
    const pages = routerConfigsInfiniteData?.pages ?? [];
    const seen = new Set<string>();
    const merged: RouterConfig[] = [];
    for (const page of pages) {
      for (const t of page.results ?? []) {
        if (!seen.has(t.configId)) {
          seen.add(t.configId);
          merged.push(t);
        }
      }
    }
    return merged;
  }, [routerConfigsInfiniteData?.pages]);

  const totalRouterConfigCount = routerConfigsInfiniteData?.pages[0]?.totalCount ?? 0;

  const [searchTerm, setSearchTerm] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewConfig, setViewConfig] = useState<RouterConfig | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [duplicateFrom, setDuplicateFrom] = useState<RouterConfig | null>(null);

  const filteredConfigs = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return mergedRouterConfigs;
    return mergedRouterConfigs.filter((config) => {
      const settings = parseRouterConfigSettings(config.routerConfigJson);
      const ssids = settings?.networks?.flatMap((n) => n.basicServiceSets?.map((bss) => bss.ssid) ?? []) ?? [];
      return [config.nickname, config.configId, ...ssids].some((value) => value?.toLowerCase().includes(q));
    });
  }, [mergedRouterConfigs, searchTerm]);

  const openView = (config: RouterConfig) => {
    setViewConfig(config);
    setViewDialogOpen(true);
  };

  const closeView = () => {
    setViewDialogOpen(false);
    setViewConfig(null);
  };

  const openCreate = (source: RouterConfig | null = null) => {
    setDuplicateFrom(source);
    setCreateDialogOpen(true);
  };

  const closeCreate = () => {
    setCreateDialogOpen(false);
    setDuplicateFrom(null);
  };

  const duplicateFromView = () => {
    if (!viewConfig) return;
    const source = viewConfig;
    closeView();
    openCreate(source);
  };

  return (
    <div className="space-y-4">
      <div className="p-4 flex items-center justify-between rounded-lg border border-border bg-muted">
        <div>
          <h1 className="text-3xl font-bold">Routers Wi-Fi Configs</h1>
          <p className="text-lg">Manage reusable Wi-Fi / bypass configs for this account.</p>
        </div>
        {canManage && (
          <Button className="gap-2" onClick={() => openCreate()}>
            <Plus className="h-4 w-4" />
            New Wi-Fi Config
          </Button>
        )}
      </div>

      <SearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search configs by name, ID, or SSID..."
      />

      {isLoadingRouterConfigs ? (
        <div className="flex justify-center items-center gap-2 py-10">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">Loading router Wi-Fi configs...</p>
        </div>
      ) : filteredConfigs.length === 0 ? (
        <EmptyView
          icon={Wifi}
          title={searchTerm ? 'No matching Wi-Fi configs' : 'No Wi-Fi configs'}
          description={
            searchTerm
              ? 'Try adjusting your search criteria.'
              : 'Create a Starlink Wi-Fi or bypass profile, then apply it to routers.'
          }
          actionLabel={canManage && !searchTerm ? 'New Wi-Fi Config' : undefined}
          onAction={canManage && !searchTerm ? () => openCreate() : undefined}
        />
      ) : (
        <>
          {filteredConfigs.map((config) => (
            <RouterConfigCard
              key={config.configId}
              config={config}
              onView={() => openView(config)}
              onDuplicate={canManage ? () => openCreate(config) : undefined}
            />
          ))}

          <div className="flex flex-row gap-3 border border-border p-4 items-center justify-between rounded-lg">
            <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
              <span className="text-md">
                Loaded <span className="font-medium">{filteredConfigs.length}</span>
                {' '}of <span className="font-bold">{totalRouterConfigCount}</span> configs
              </span>
              {filteredConfigs.length !== mergedRouterConfigs.length && (
                <span className="text-sm">
                  ({filteredConfigs.length} match current search query)
                </span>
              )}
            </div>
            {hasNextRouterConfigs ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 gap-2 self-start sm:self-auto"
                disabled={isFetchingNextRouterConfigs}
                onClick={() => void fetchNextRouterConfigs()}
              >
                {isFetchingNextRouterConfigs ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading…
                  </>
                ) : (
                  'Load more'
                )}
              </Button>
            ) : null}
          </div>
        </>
      )}

      <RouterConfigDialog
        open={viewDialogOpen}
        onOpenChange={(open) => !open && closeView()}
        mode="view"
        config={viewConfig}
        onDuplicate={canManage ? duplicateFromView : undefined}
      />

      <RouterConfigDialog
        open={createDialogOpen}
        onOpenChange={(open) => !open && closeCreate()}
        mode={duplicateFrom ? 'duplicate' : 'create'}
        config={duplicateFrom}
      />
    </div>
  );
};
